import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery
} from '@mui/material';
import {
  AccountBox as PortfolioIcon,
  Flag as GoalsIcon,
  School as LearningIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { THEME_COLORS } from '../../styles/theme';
import ProfessionalPortfolio from './components/ProfessionalPortfolio';
import ProfessionalDevelopmentGoals from './components/PDGoals';
import ProfessionalLearning from './components/PDLearning';
import {
  getAudienceLabel,
  getProfessionalDevelopmentRecord,
  inferProfessionalAudience
} from './services/professionalDevelopmentService';

const summaryCardSx = {
  height: '100%',
  borderRadius: 3,
  border: '1px solid rgba(39, 35, 97, 0.08)'
};

const ProfessionalDevelopment = () => {
  const [activeTab, setActiveTab] = useState('development');
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState('');
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const loadRecord = useCallback(async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const data = await getProfessionalDevelopmentRecord(currentUser.uid, userProfile || {});
      setRecord(data);
    } catch (loadError) {
      console.error('Error loading professional development record:', loadError);
      setError('Unable to load professional development right now.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, userProfile]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const audience = record?.audience || inferProfessionalAudience(userProfile || {});
  const audienceLabel = getAudienceLabel({ ...(userProfile || {}), audience });
  const goalCount = record?.goals?.length || 0;
  const completedGoals = (record?.goals || []).filter((goal) => Number(goal.progress || 0) >= 100).length;
  const activityHours = (record?.activities || []).reduce((sum, activity) => sum + Number(activity.duration || 0), 0);
  const activeLearning = (record?.enrolledPaths || []).filter((path) => Number(path.progress || 0) < 100).length;

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={6}>
          <CircularProgress sx={{ color: THEME_COLORS.primary }} />
        </Box>
      );
    }

    switch (activeTab) {
      case 'portfolio':
        return <ProfessionalPortfolio userId={currentUser?.uid} />;
      case 'learning':
        return (
          <ProfessionalLearning
            userId={currentUser?.uid}
            userProfile={userProfile}
            record={record}
            onDataChanged={loadRecord}
          />
        );
      case 'development':
      default:
        return (
          <ProfessionalDevelopmentGoals
            userId={currentUser?.uid}
            userProfile={userProfile}
            record={record}
            onDataChanged={loadRecord}
          />
        );
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 3,
          borderRadius: 4,
          color: 'white',
          background: 'linear-gradient(135deg, #272361 0%, #3949ab 55%, #5c6bc0 100%)'
        }}
      >
        <Chip
          label={`${audienceLabel} pathway`}
          sx={{
            mb: 2,
            color: THEME_COLORS.primary,
            bgcolor: 'rgba(255,255,255,0.92)',
            fontWeight: 700
          }}
        />
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Professional Development Hub
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 760, opacity: 0.94 }}>
          Track growth goals, log professional learning, and build a defensible development record for classroom practice and school leadership.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <Card sx={summaryCardSx}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Goals</Typography>
                <Typography variant="h4" fontWeight={800}>{goalCount}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {completedGoals} completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Card sx={summaryCardSx}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Learning Paths</Typography>
                <Typography variant="h4" fontWeight={800}>{record?.enrolledPaths?.length || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeLearning} currently active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Card sx={summaryCardSx}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Logged Hours</Typography>
                <Typography variant="h4" fontWeight={800}>{activityHours}</Typography>
                <Typography variant="body2" color="text.secondary">
                  hours of professional activity
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Card sx={summaryCardSx}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Priority Areas</Typography>
                <Typography variant="h6" fontWeight={800}>{record?.priorityAreas?.length || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  role-aligned development focus areas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            '& .MuiTab-root': {
              minHeight: 52,
              textTransform: 'none',
              fontWeight: 700
            },
            '& .MuiTabs-indicator': {
              backgroundColor: THEME_COLORS.primary
            }
          }}
        >
          <Tab icon={<GoalsIcon />} iconPosition="start" label="Development Goals" value="development" />
          <Tab icon={<LearningIcon />} iconPosition="start" label="Learning Pathways" value="learning" />
          <Tab icon={<PortfolioIcon />} iconPosition="start" label="Professional Portfolio" value="portfolio" />
        </Tabs>
      </Paper>

      <Box>{renderContent()}</Box>
    </Container>
  );
};

export default ProfessionalDevelopment;
